// src/components/parent-dashboard/api/apiService.js

/**
 * API Service for Parent Dashboard
 * Contains all API calls to the backend
 */

import { API_BASE_URL } from "../../../config";

const apiService = {
    // Link a child
    linkChild: async (childEmail) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/link-child`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ child_email: childEmail })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to link child");
            }

            return await response.json();
        } catch (error) {
            console.error("Error linking child:", error);
            throw error;
        }
    },

    // Get all linked children
    getChildren: async () => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch children");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching children:", error);
            throw error;
        }
    },

    // Get child's classes
    getChildClasses: async (childId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/classes`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch child's classes");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching child's classes:", error);
            throw error;
        }
    },

    // Get child's teachers
    getChildTeachers: async (childId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/teachers`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch child's teachers");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching child's teachers:", error);
            throw error;
        }
    },

    // Get child's progress
    getChildProgress: async (childId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/progress`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch child's progress");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching child's progress:", error);
            throw error;
        }
    },

    // Get child's assignments
    getChildAssignments: async (childId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/assignments`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch child's assignments");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching child's assignments:", error);
            throw error;
        }
    },

    // Get child's submission for an assignment
    getChildSubmission: async (childId, assignmentId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/assignments/${assignmentId}/submission`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch child's submission");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching child's submission:", error);
            throw error;
        }
    },

    // Get child's attendance
    getChildAttendance: async (childId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/attendance`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch child's attendance");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching child's attendance:", error);
            throw error;
        }
    },

    // Get child's attendance overview for a class
    getChildAttendanceOverview: async (childId, classId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/attendance/overview?class_id=${classId}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch child's attendance overview");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching child's attendance overview:", error);
            throw error;
        }
    },

    // Add caregiver observation
    addCaregiverObservation: async (childId, assignmentId, payload) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/assignments/${assignmentId}/submission/caregiver-feedback`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to add caregiver observation");
            }

            return await response.json();
        } catch (error) {
            console.error("Error adding caregiver observation:", error);
            throw error;
        }
    },

    // Upload child profile picture
    uploadChildProfilePicture: async (childId, file) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/profile-picture`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${accessToken}`
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to upload profile picture");
            }

            return await response.json();
        } catch (error) {
            console.error("Error uploading profile picture:", error);
            throw error;
        }
    },

    // Get child profile picture
    getChildProfilePicture: async (childId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/children/${childId}/profile-picture`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    return null; // No profile picture set
                }
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to get profile picture");
            }

            return await response.json();
        } catch (error) {
            console.error("Error getting profile picture:", error);
            if (error.message.includes("404")) {
                return null; // No profile picture set
            }
            throw error;
        }
    },

    // Get parent profile picture
    getParentProfilePicture: async () => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/parent/profile-picture`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    return null; // No profile picture set
                }
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to get parent profile picture");
            }

            return await response.json();
        } catch (error) {
            console.error("Error getting parent profile picture:", error);
            if (error.message.includes("404")) {
                return null; // No profile picture set
            }
            throw error;
        }
    },

    // Upload parent profile picture
    uploadParentProfilePicture: async (file) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/parent/profile-picture`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${accessToken}`
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to upload parent profile picture");
            }

            return await response.json();
        } catch (error) {
            console.error("Error uploading parent profile picture:", error);
            throw error;
        }
    }
};

export default apiService;